// CONFORM.TOOLS DataCalc
// CONFORM.TOOLS | datacalc@tbdpost.com

import React, { useState, useRef, useEffect, useCallback, Suspense } from 'react';
import { Helmet } from 'react-helmet-async';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import Header from './components/Header';
import Log from './components/Log';
import SideMenu from './components/SideMenu';
import Footer from './components/Footer';
import MobileNavBar from './components/MobileNavBar';
import PrivacyPolicy from './components/PrivacyPolicy';
import './App.css';
import { CalculatorProvider } from './context/CalculatorContext';
import { deviceTypes, breakpoints } from './styles/globalStyles';
import { calculatorConfig, landingPageInfo, isInBetaEnvironment, isEmbeddedMode } from './config/features';
import { BRAND_APP } from './config/brand';
import LandingPage from './components/LandingPage';
import NotFound from './components/NotFound';
import SEOContentSection from './components/SEOContentSection';

import {
  AspectRatioCalculator,
  BitrateCalculator,
  CameraToFileSize,
  ComingSoon,
  DailiesCalculator,
  DurationToFileSize,
  FilmCalculator,
  TransferTimeCalculator,
  TimecodeGraphingCalculator,
  FramingChartGenerator,
  AnalyticsDashboard,
  CameraSpecsApp,
  SpecViewer,
  RequestPage,
} from './routes';
import { initializeAnalytics, trackPageView, trackCalculationLog, trackShare, updateAnalyticsConsent } from './utils/analytics';
import SEOHead from './components/SEOHead';
import CookieConsent from './components/CookieConsent';
import { ThemeProvider } from './context/ThemeContext';
import { ScrollText, ChevronRight } from 'lucide-react';

const getBasename = () => {
  if (window.location.hostname === 'beta.data-calc.com') return '/';
  if (isInBetaEnvironment()) return '/beta';
  return '/';
};

// Wraps children in Suspense only once `active` — see suspenseReady in App.
const MaybeSuspense = ({ active, fallback, children }) =>
  active ? <Suspense fallback={fallback}>{children}</Suspense> : children;

// The first client render must exactly match the HTML react-snap baked at
// build time (480px viewport, empty storage), or React 18 logs hydration
// errors (#418/#423/#425) and throws the server HTML away. Real values are
// applied in effects immediately after mount.
const PRERENDER_DEVICE_STATE = {
  isMobile: true,
  isTablet: false,
  isTouchDevice: false,
  isWideScreen: false,
  isLandscape: false,
};

function App() {
  // ── Device state (deterministic for hydration; corrected on mount) ──
  const [deviceState, setDeviceState] = useState(PRERENDER_DEVICE_STATE);

  // ── Panel visibility (persisted; defaults match prerendered HTML,
  //     restored from localStorage after hydration) ──
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false); // default: expanded on desktop
  const [logOpen, setLogOpen] = useState(true); // default: visible on desktop

  // Mobile drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Other state
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [logs, setLogs] = useState([]);
  const hydratedRef = useRef(false);

  // Suspense boundaries can't hydrate react-snap HTML (it lacks React's
  // <!--$--> SSR markers, so hydration bails and repaints the whole route —
  // tanking LCP). The initial route's chunk is preloaded by index.js and
  // renders synchronously, so no boundary is needed until after mount, when
  // client-side navigations can lazily load other routes.
  const [suspenseReady, setSuspenseReady] = useState(false);
  useEffect(() => { setSuspenseReady(true); }, []);
  const logRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const [previousPath, setPreviousPath] = useState('/duration');

  // Behaviour only (touch handlers, Log density). Layout uses CSS breakpoints.
  const { isMobile } = deviceState;
  const isEmbedMode = isEmbeddedMode();

  // ── Derived page info ──
  // Internal pages that exist as routes but not in calculatorConfig.
  const internalPageInfo = {
    '/analytics': { path: '/analytics', title: 'Analytics Dashboard', metaTitle: `Analytics | ${BRAND_APP}`, description: '', noindex: true, hideTitleCard: true },
    '/comingsoon': { path: '/comingsoon', title: 'Coming Soon', metaTitle: `Coming Soon | ${BRAND_APP}`, description: '', noindex: true, hideTitleCard: true },
  }[location.pathname];

  // Unknown routes get noindex meta and no canonical — never fall back to a
  // calculator's page info (that made every bogus URL emit /duration's SEO tags).
  const notFoundPageInfo = {
    path: location.pathname,
    title: 'Page Not Found',
    metaTitle: `Page Not Found | ${BRAND_APP}`,
    description: "The page you're looking for doesn't exist.",
    noindex: true,
    hideTitleCard: true,
  };

  const currentPageInfo = location.pathname === '/'
    ? landingPageInfo
    : Object.values(calculatorConfig).find(
        calc => calc.path === location.pathname
      ) || Object.values(calculatorConfig).find(
        calc => location.pathname.startsWith(calc.path + '/')
      ) || internalPageInfo || notFoundPageInfo;

  const currentCalculatorKey = Object.entries(calculatorConfig).find(
    ([, calc]) => calc.path === location.pathname || location.pathname.startsWith(calc.path + '/')
  )?.[0] || null;

  const isCameraSpecsPage = location.pathname.startsWith('/cameraspecs');
  const isLogsPage = location.pathname === '/logs';
  const isAnalyticsPage = location.pathname === '/analytics';
  const isRequestPage = location.pathname === '/request';
  const hideLogPanel = isLogsPage || isAnalyticsPage || isCameraSpecsPage || isRequestPage;

  // ── Restore persisted UI state after hydration ──
  useEffect(() => {
    const savedSidebar = localStorage.getItem('sidebar_collapsed');
    if (savedSidebar !== null) setSidebarCollapsed(JSON.parse(savedSidebar));
    const savedLog = localStorage.getItem('log_visible');
    if (savedLog !== null) setLogOpen(JSON.parse(savedLog));
    const savedLogs = localStorage.getItem('calculatorLogs');
    if (savedLogs) setLogs(JSON.parse(savedLogs));
  }, []);

  // ── Persist panel preferences (skipped until restore has run, so the
  //     mount pass doesn't clobber stored values with defaults) ──
  useEffect(() => {
    if (!hydratedRef.current) return;
    localStorage.setItem('sidebar_collapsed', JSON.stringify(sidebarCollapsed));
  }, [sidebarCollapsed]);

  useEffect(() => {
    if (!hydratedRef.current) return;
    localStorage.setItem('log_visible', JSON.stringify(logOpen));
  }, [logOpen]);

  // ── Resize handler ──
  useEffect(() => {
    const handleResize = () => {
      setDeviceState({
        isMobile: deviceTypes.isMobile(),
        isTablet: deviceTypes.isTablet(),
        isTouchDevice: deviceTypes.isTouchDevice(),
        isWideScreen: window.innerWidth >= breakpoints.desktop,
        isLandscape: window.innerWidth > window.innerHeight
      });
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Defer GA (163 KB of third-party JS) until the browser is idle so it
  // never competes with first paint or hydration.
  useEffect(() => {
    if ('requestIdleCallback' in window) {
      const id = window.requestIdleCallback(() => initializeAnalytics(), { timeout: 4000 });
      return () => window.cancelIdleCallback(id);
    }
    const t = setTimeout(() => initializeAnalytics(), 2500);
    return () => clearTimeout(t);
  }, []);

  // ── Log management ──
  const addLog = useCallback((entry) => {
    setLogs(prevLogs => [entry, ...prevLogs]);
    const calculatorType = currentPageInfo.title || 'Unknown Calculator';
    trackCalculationLog(calculatorType, entry);
  }, [currentPageInfo.title]);

  const deleteLog = (index) => {
    setLogs(prevLogs => prevLogs.filter((_, i) => i !== index));
  };

  const handleShare = (log) => {
    trackShare('log_entry', 'native_share');
    if (navigator.share) {
      navigator.share({ title: 'DataCalc Log Entry', text: log }).catch(() => {});
    }
  };

  useEffect(() => {
    if (!hydratedRef.current) return;
    localStorage.setItem('calculatorLogs', JSON.stringify(logs));
  }, [logs]);

  // Marks restore complete — declared after all persist effects so they
  // stay inert during the mount flush.
  useEffect(() => {
    hydratedRef.current = true;
  }, []);

  useEffect(() => {
    if (location.pathname !== '/logs') {
      setPreviousPath(location.pathname);
    }
    trackPageView(location.pathname, currentPageInfo.title);
  }, [location.pathname, currentPageInfo.title]);

  // Layout switches on the `md:` breakpoint (768px), which is the exact
  // complement of deviceTypes.isMobile (<= 767px). Deciding in JS meant the
  // desktop shell only appeared after hydration, and since react-snap
  // prerenders at 480px every desktop visitor got one large layout shift
  // (measured CLS 0.18). isMobile is still used for behaviour, never layout.
  const showLogPanel = logOpen && !hideLogPanel;

  return (
    <ThemeProvider>
    <CalculatorProvider>
      <SEOHead pageInfo={currentPageInfo} calculatorKey={currentCalculatorKey} />
      <div className="app h-[100dvh] flex flex-col bg-surface-page overflow-hidden">
        {/* ── Fixed header ── */}
        {!isEmbedMode && (
          <div className="fixed top-0 left-0 right-0 z-30">
            <Header
              onToggleMenu={() => setDrawerOpen(prev => !prev)}
              pageTitle={currentPageInfo.title}
            />
          </div>
        )}

        {/* ── Mobile drawer overlay ── */}
        {!isEmbedMode && (
          <div className="md:hidden">
            <SideMenu
              isDrawer={true}
              isOpen={drawerOpen}
              onClose={() => setDrawerOpen(false)}
            />
          </div>
        )}

        {/* ── Main content area ── */}
        <div
          className={`flex-1 min-h-0 w-full px-2 pt-2 pb-2 md:px-4 md:pt-3 md:pb-3 ${isEmbedMode ? 'mb-[44px] md:mb-0 mt-0' : 'mb-[82px] md:mb-[38px] mt-[44px] md:mt-[48px]'} overflow-y-auto overflow-x-hidden overscroll-contain touch-pan-y`}
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          <div className="max-w-[1920px] mx-auto flex items-start gap-3">

            {/* ── Desktop sidebar (self-contained collapse/expand + log toggle) ── */}
            <div className="hidden md:block flex-shrink-0">
              <div className="sticky" style={{ top: 0 }}>
                  <SideMenu
                    isDrawer={false}
                    collapsed={sidebarCollapsed}
                    onCollapse={() => setSidebarCollapsed(true)}
                    onExpand={() => setSidebarCollapsed(false)}
                    logOpen={logOpen}
                    onToggleLog={() => setLogOpen(prev => !prev)}
                    logCount={logs.length}
                  hideLogToggle={hideLogPanel}
                />
              </div>
            </div>

            {/* ── Calculator + Log columns ── */}
            <div className={`flex flex-col ${
              showLogPanel ? 'md:flex-row' : 'md:flex-col'
            } gap-3 w-full min-w-0`}>

              {/* Calculator column */}
              <div className="flex flex-col flex-grow min-w-0 md:gap-3">

                {/* Page title card -- hidden on landing page (it has its own hero) and on
                    noindex pages. Shown on mobile too: Google crawls mobile-first, so the
                    H1 + description must exist in the mobile render. Embed keeps its old
                    desktop-only behavior. */}
                {location.pathname !== '/' && !currentPageInfo.hideTitleCard && (
                  <div className={`px-5 bg-surface-card rounded-xl border border-border py-2 mb-2 md:py-3 md:mb-0 ${isEmbedMode ? 'hidden md:block' : ''}`}>
                    <h1 className="font-bold text-ink mb-0.5 text-base md:text-lg">{currentPageInfo.title}</h1>
                    <p className="text-ink-secondary leading-relaxed text-xs md:text-sm">{currentPageInfo.description}</p>
                  </div>
                )}

                {/* Calculator content */}
                <div className={`${
                  isLogsPage
                    ? "p-2"
                    : isCameraSpecsPage
                      ? "bg-surface-card rounded-xl border border-border text-left"
                      : "p-2 md:p-4 bg-surface-card rounded-xl border border-border text-center"
                }`}>
                  <MaybeSuspense
                    active={suspenseReady}
                    fallback={<div className="py-16 text-center text-sm text-ink-tertiary">Loading…</div>}
                  >
                  <Routes>
                    <Route path="/" element={<LandingPage />} />
                    <Route path="/duration" element={<DurationToFileSize addLog={addLog} />} />
                    <Route path="/aspect-ratio" element={<AspectRatioCalculator addLog={addLog} />} />
                    <Route path="/bitrate" element={<BitrateCalculator addLog={addLog} />} />
                    <Route path="/bitrate-calculator" element={<Navigate to="/bitrate" replace />} />
                    <Route path="/dailies" element={<DailiesCalculator addLog={addLog} />} />
                    <Route path="/dailies-calculator" element={<Navigate to="/dailies" replace />} />
                    <Route path="/dailies-to-file-size" element={<Navigate to="/dailies" replace />} />
                    <Route path="/dailies-to-file-size-calculator" element={<Navigate to="/dailies" replace />} />
                    <Route path="/file-size-calculator" element={<Navigate to="/duration" replace />} />
                    <Route path="/video-duration-to-file-size-calculator" element={<Navigate to="/duration" replace />} />
                    <Route path="/camera" element={<CameraToFileSize addLog={addLog} />} />
                    <Route path="/camera-to-file-size-calculator" element={<Navigate to="/camera" replace />} />

                    <Route path="/film" element={<FilmCalculator addLog={addLog} />} />
                    <Route path="/film-calculator" element={<Navigate to="/film" replace />} />
                    <Route path="/motion-picture-film" element={<Navigate to="/film" replace />} />
                    <Route path="/motion-picture-film-calculator" element={<Navigate to="/film" replace />} />
                    <Route path="/timecode" element={<TimecodeGraphingCalculator addLog={addLog} />} />
                    <Route path="/timecode-calculator" element={<Navigate to="/timecode" replace />} />
                    <Route path="/timecodecalculator" element={<Navigate to="/timecode" replace />} />
                    <Route path="/transfer-time" element={<TransferTimeCalculator addLog={addLog} />} />
                    <Route path="/transfer-time-calculator" element={<Navigate to="/transfer-time" replace />} />
                    {/* SEO redirect routes for popular search terms */}
                    <Route path="/video-space-calculator" element={<Navigate to="/duration" replace />} />
                    <Route path="/video-file-size-calculator" element={<Navigate to="/duration" replace />} />
                    <Route path="/video-storage-calculator" element={<Navigate to="/duration" replace />} />
                    <Route path="/video-size-calculator" element={<Navigate to="/duration" replace />} />
                    <Route path="/video-size-estimator" element={<Navigate to="/duration" replace />} />
                    <Route path="/storage-calculator" element={<Navigate to="/duration" replace />} />
                    <Route path="/camera-storage-calculator" element={<Navigate to="/camera" replace />} />
                    <Route path="/recording-time-calculator" element={<Navigate to="/camera" replace />} />
                    <Route path="/video-bitrate-calculator" element={<Navigate to="/bitrate" replace />} />
                    <Route path="/aspect-ratio-calculator" element={<Navigate to="/aspect-ratio" replace />} />
                    <Route path="/framing-chart-generator" element={<Navigate to="/framing-chart" replace />} />
                    <Route path="/data-transfer-calculator" element={<Navigate to="/transfer-time" replace />} />
                    <Route path="/film-stock-calculator" element={<Navigate to="/film" replace />} />
                    <Route path="/request" element={<RequestPage />} />
                    <Route path="/comingsoon" element={<ComingSoon />} />
                    <Route path="/logs" element={
                      <>
                      <Helmet>
                        <meta name="robots" content="noindex, nofollow" />
                      </Helmet>
                      <div className="flex flex-col md:max-w-2xl md:mx-auto lg:max-w-none lg:mx-0">
                        <div className="md:hidden">
                          <div className="sticky mb-2">
                            <button onClick={() => navigate(-1)} className="w-full flex justify-center py-2.5 px-4 bg-conform-red-deep text-white rounded-lg hover:bg-conform-red-accent transition-colors font-medium text-sm">
                              Back
                            </button>
                          </div>
                        </div>
                        <Log
                          log={logs}
                          onDelete={deleteLog}
                          onShare={handleShare}
                          isMobile={isMobile}
                          isFullPage={true}
                          previousCalculator={previousPath}
                        />
                      </div>
                      </>
                    } />
                    <Route path="/framing-chart" element={<FramingChartGenerator addLog={addLog} />} />
                    <Route path="/analytics" element={<AnalyticsDashboard />} />
                    <Route path="/cameraspecs" element={<CameraSpecsApp />} />
                    <Route path="/cameraspecs/spec" element={<SpecViewer />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                  </MaybeSuspense>
                </div>

                {/* Crawlable SEO content below the calculator (not in embeds) */}
                {!isEmbedMode && currentCalculatorKey && (
                  <SEOContentSection calculatorKey={currentCalculatorKey} />
                )}
              </div>

              {/* ── Log panel (desktop/tablet, open) ── */}
              {showLogPanel && (
                <div className="hidden md:flex flex-col w-[340px] flex-shrink-0">
                  {/* Log header with close button */}
                  <div className="flex items-center justify-between p-3 mb-2 bg-surface-card rounded-xl border border-border">
                    <div className="flex items-center gap-2">
                      <ScrollText className="w-4 h-4 text-ink-secondary" />
                      <h2 className="text-sm font-semibold text-ink">Log</h2>
                      {logs.length > 0 && (
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-conform-red/5 text-conform-red-accent">
                          {logs.length}
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => setLogOpen(false)}
                      className="flex items-center justify-center w-7 h-7 rounded-lg text-ink-tertiary hover:bg-hover hover:text-ink transition-colors"
                      title="Hide log panel"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                  {/* Log entries */}
                  <div className="flex-1 overflow-visible">
                    <Log
                      log={logs}
                      onDelete={deleteLog}
                      onShare={handleShare}
                      isMobile={false}
                    />
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>

        {/* ── Fixed bottom: nav bar + footer ── */}
        <div className={`fixed bottom-0 left-0 right-0 z-20 ${isEmbedMode ? 'md:hidden' : ''}`}>
          <div className="md:hidden">
            <MobileNavBar />
          </div>
          {!isEmbedMode && (
            <Footer setShowPrivacyPolicy={setShowPrivacyPolicy} />
          )}
        </div>

        {showPrivacyPolicy && (
          <PrivacyPolicy onClose={() => setShowPrivacyPolicy(false)} />
        )}

        <CookieConsent onConsent={(choice) => updateAnalyticsConsent(choice)} />
      </div>
    </CalculatorProvider>
    </ThemeProvider>
  );
}

export default App;
